// conectagran/frontend/src/api/hooks/useGam.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

const BASE = '/gam'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GamConnection {
  connected: boolean
  google_email?: string
  network_code?: string
  last_synced_at?: string
}

export interface GamAdUnit {
  id: string
  ad_unit_id: string
  ad_unit_name: string
  finance_page_id: string | null
  finance_page_name: string | null
}

export interface GamReportSummary {
  ecpm: number
  revenue_usd: number
  revenue_brl: number
  impressions: number
  code_served: number
  unfilled: number
  fill_rate: number
  ctr: number
  peak_ecpm: number
  peak_label: string
  today_revenue_usd: number
  today_revenue_brl: number
  delta_ecpm: number | null
  delta_revenue: number | null
  delta_impressions: number | null
  delta_ctr: number | null
  delta_fill_rate: number | null
  delta_period: string
}

export interface GamReportRow {
  label: string
  impressions: number
  clicks: number
  code_served?: number
  unfilled?: number
  fill_rate?: number
  status?: 'active' | 'idle' | 'inactive'
  ctr: number
  ecpm: number
  revenue_brl: number
}

export interface GamChartPoint {
  time: number      // Unix seconds
  ecpm: number
  impressions: number
}

export interface GamReport {
  summary: GamReportSummary
  rows: GamReportRow[]
  chart: GamChartPoint[]
  usd_brl_rate?: number
  last_synced_at?: string
}

export type GamDimension = 'hour' | 'day' | 'ad_unit' | 'finance_page' | 'device'

// ─── Connection ───────────────────────────────────────────────────────────────

export function useGamConnection() {
  return useQuery<GamConnection>({
    queryKey: ['gam', 'connection'],
    queryFn: () => api.get<GamConnection>(`${BASE}/connection`),
  })
}

export function useSaveGamConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (networkCode: string) =>
      api.post(`${BASE}/connection`, { network_code: networkCode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gam', 'connection'] }),
  })
}

export function useDeleteGamConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete(`${BASE}/connection`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gam', 'connection'] })
      qc.invalidateQueries({ queryKey: ['gam', 'ad-units'] })
    },
  })
}

export function useSyncGam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`${BASE}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gam'] }),
  })
}

// ─── Ad Units ─────────────────────────────────────────────────────────────────

export function useGamAdUnits() {
  return useQuery<{ ad_units: GamAdUnit[] }>({
    queryKey: ['gam', 'ad-units'],
    queryFn: () => api.get<{ ad_units: GamAdUnit[] }>(`${BASE}/ad-units`),
  })
}

export function useDiscoverGamAdUnits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ count: number }>(`${BASE}/ad-units/discover`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gam', 'ad-units'] }),
  })
}

export function useLinkGamAdUnit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, financePageId }: { id: string; financePageId: string | null }) =>
      api.put(`${BASE}/ad-units/${id}/link`, { finance_page_id: financePageId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gam', 'ad-units'] }),
  })
}

// ─── Report ───────────────────────────────────────────────────────────────────

// ─── URL Report ───────────────────────────────────────────────────────────────

export interface GamUrlRow {
  url: string
  impressions: number
  clicks: number
  revenue_usd: number
  revenue_brl: number
  ecpm: number
  ctr: number
}

export interface GamUrlReport {
  status: 'pending' | 'ready' | 'error'
  rows?: GamUrlRow[]
  usd_brl_rate?: number
  message?: string
}

export function useGamUrlReport(params: { from: string; to: string; enabled: boolean }) {
  return useQuery<GamUrlReport>({
    queryKey: ['gam', 'url-report', params.from, params.to],
    queryFn: () => {
      const qs = new URLSearchParams({ from: params.from, to: params.to })
      return api.get<GamUrlReport>(`${BASE}/url-report?${qs}`)
    },
    enabled: params.enabled && !!params.from && !!params.to,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? 8000 : false,
  })
}

export function useGamReport(params: {
  financePageId?: string
  from: string
  to: string
  fromHour?: number
  toHour?: number
  dimension: GamDimension
  enabled?: boolean
}) {
  return useQuery<GamReport>({
    queryKey: ['gam', 'report', params],
    queryFn: () => {
      const qs = new URLSearchParams({
        from: params.from,
        to: params.to,
        dimension: params.dimension,
      })
      if (params.financePageId)           qs.set('finance_page_id', params.financePageId)
      if (params.fromHour !== undefined)  qs.set('from_hour', String(params.fromHour))
      if (params.toHour   !== undefined)  qs.set('to_hour',   String(params.toHour))
      return api.get<GamReport>(`${BASE}/report?${qs.toString()}`)
    },
    enabled: (params.enabled ?? true) && !!params.from && !!params.to,
  })
}

// ─── Peak Hours ──────────────────────────────────────────────────────────────

export interface GamPeakHour {
  hour: number
  time: string
  ecpm: number
  views: number
  uniques: number
  score: number
}

export interface GamPeakHoursResponse {
  hours: GamPeakHour[]
  avgEcpm: number
  hasData: boolean
  period: { from: string; to: string }
}

export function useGamPeakHours() {
  return useQuery<GamPeakHoursResponse>({
    queryKey: ['gam-peak-hours'],
    queryFn: () => api.get<GamPeakHoursResponse>(`${BASE}/peak-hours`),
    staleTime: 1000 * 60 * 30, // 30 min cache
  })
}
